import { WidgetId } from 'types/idTypes';
import { WidgetsMap } from 'types/infographTypes';
import { Widget, WidgetType } from 'types/widget.types';
import { IconWidgetData, IconWidgetType } from 'widgets/IconWidget/IconWidget.types';
import { ResponsiveWidgetBaseData } from 'widgets/ResponsiveWidgets/ResponsiveWidgetBase.types';
import { RESPONSIVE_WIDGET_BASE_WIDGETS } from 'widgets/Widget.config';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';

import { StatChartType, StatChartWidgetData } from './StatChartWidget/StatChartWidget.types';

/**
 * Checks if a given widgetId corresponds to a parent responsive widget base
 * E.g. members will return false, but the parent will return true
 *
 * @param {WidgetId} widgetId - The ID of the widget to check.
 * @returns {boolean}
 */
export const isResponsiveWidgetBase = (widgetId: WidgetId) => {
  const widgetType = getWidgetTypeFromId(widgetId);

  return !!RESPONSIVE_WIDGET_BASE_WIDGETS[widgetType];
};

/**
 * Checks if a given widgetId corresponds to a responsive member
 *
 * @param {WidgetId} widgetId - The ID of the widget to check.
 * @param {WidgetsMap} widgets - Map of all infograph widgets
 * @returns {boolean}
 */
export const isResponsiveWidgetMember = (widgetId: WidgetId, widgets: WidgetsMap) => {
  // TODO: this could be updated to check the pageOrder instead of the widget type
  // currently these widget types are only used for stat charts
  const widgetType = getWidgetTypeFromId(widgetId);
  if (widgetType === WidgetType.LabelText || widgetType === WidgetType.ProgressChart) return true;
  if ((widgets?.[widgetId] as IconWidgetData)?.type === IconWidgetType.Grid) return true;

  return false;
};

/**
 * Checks if a given widgetId corresponds to a responsive widget base or member
 *
 * @param {WidgetId} widgetId - The ID of the widget to check.
 * @param {WidgetsMap} widgets - Map of all infograph widgets
 * @returns {boolean}
 */
export const isResponsiveWidget = (widgetId: WidgetId, widgets: WidgetsMap) => {
  if (isResponsiveWidgetBase(widgetId)) return true;
  if (isResponsiveWidgetMember(widgetId, widgets)) return true;

  return false;
};

/**
 * Returns the visible member widgets of a responsive widget group - i.e. the widgets that are not hidden.
 *
 * @param {WidgetsMap} widgets - Map of all infograph widgets
 * @param {WidgetId} responsiveGroupId - The ID of the responsive widget base
 * @returns {WidgetId[]}
 *
 */
const getVisibleMemberWidgets = (widgets: WidgetsMap, responsiveGroupId: WidgetId) => {
  const responsiveWidget = widgets[responsiveGroupId] as ResponsiveWidgetBaseData;
  const memberWidgetIds = responsiveWidget?.memberWidgetIds || [];
  const visibleMemberWidgetIds = memberWidgetIds.filter((wId: WidgetId) => !widgets[wId].isHidden);

  return visibleMemberWidgetIds;
};

/**
 * Checks if a given widget is the last responsive member in a responsive widget group.
 * E.g. for tabbing through widgets
 *
 * @param {WidgetsMap} widgets - Map of all infograph widgets
 * @param {WidgetId} id - The ID of the widget to check - can be a responsive widget base or member
 * @param {WidgetId} responsiveGroupId - The ID of the responsive widget group.
 * @returns {boolean}
 */
export const isLastResponsiveMember = (widgets: WidgetsMap, id: WidgetId, responsiveGroupId: WidgetId) => {
  const orderedMemberIds = getVisibleMemberWidgets(widgets, responsiveGroupId);
  if (orderedMemberIds.length === 1) return true;
  if (id === responsiveGroupId) return false;

  const isLastMember = id === orderedMemberIds[orderedMemberIds.length - 1];
  return isLastMember;
};

/**
 * Determines the ID of the next visible widget in the responsive widget group.
 * E.g. for tabbing through widgets
 *
 * @param {WidgetId} responsiveWidgetBaseId - The ID of the responsive widget group.
 * @param {WidgetsMap} widgets - Map of all infograph widgets
 * @param {WidgetId} activeId - The ID of the currently active widget.
 * @returns {WidgetId}
 */
export const getNextResponsiveWidgetMember = (
  responsiveWidgetBaseId: WidgetId,
  widgets: WidgetsMap,
  activeId: WidgetId,
  // if its the first time
  isInitial = false,
) => {
  const orderedMemberIds = getVisibleMemberWidgets(widgets, responsiveWidgetBaseId);
  const isResponsiveWidgetBase = activeId === responsiveWidgetBaseId;

  // For the first time, if the responsive widget base is an icon stat chart, the last icon should be active
  const isIconStatChart = (widgets[responsiveWidgetBaseId] as StatChartWidgetData)?.type === StatChartType.Icon;
  if (isInitial && isIconStatChart) {
    return orderedMemberIds[orderedMemberIds.length - 1];
  }

  if (isResponsiveWidgetBase) return orderedMemberIds[0];

  let nextId;
  let startIndex = orderedMemberIds.findIndex((wId: WidgetId) => wId === activeId);
  let i = startIndex + 1;

  // in case the active widget is hidden, we need to loop through the member widgets until we find the next non-hidden widget
  while (nextId === undefined) {
    const nextWidgetId = orderedMemberIds[i];
    let nextWidget = widgets[nextWidgetId] as Widget;

    if (!nextWidget) i = 0;

    // skip hidden widgets not rendered in the DOM
    const isHidden = !!!document.getElementById(nextWidgetId);

    if (nextWidget && !isHidden) nextId = nextWidgetId;
    if (nextWidget && isHidden) i++;
    if (i === startIndex) nextId = activeId;
  }

  return nextId || '';
};

/**
 * Determines the previous ID of the next visible widget in the responsive widget group.
 * E.g. for tabbing through widgets
 *
 * @param {WidgetId} responsiveWidgetBaseId - The ID of the responsive widget group.
 * @param {WidgetsMap} widgets - Map of all infograph widgets
 * @param {WidgetId} activeId - The ID of the currently active widget.
 * @returns {WidgetId}
 */
export const getPreviousResponsiveWidgetMember = (
  responsiveWidgetBaseId: WidgetId,
  widgets: WidgetsMap,
  activeId: WidgetId,
) => {
  const orderedMemberIds = getVisibleMemberWidgets(widgets, responsiveWidgetBaseId);
  const isResponsiveWidgetBase = activeId === responsiveWidgetBaseId;
  if (isResponsiveWidgetBase) return orderedMemberIds[orderedMemberIds.length - 1];

  let nextId;
  let startIndex = orderedMemberIds.findIndex((wId: WidgetId) => wId === activeId);
  let i = startIndex - 1;

  // in case the active widget is hidden, we need to loop through the member widgets until we find the next non-hidden widget
  while (nextId === undefined) {
    const nextWidgetId = orderedMemberIds[i];
    let nextWidget = widgets[nextWidgetId] as Widget;

    if (!nextWidget) i = orderedMemberIds.length - 1;

    // skip hidden widgets not rendered in the DOM
    const isHidden = !!!document.getElementById(nextWidgetId);

    if (nextWidget && !isHidden) nextId = nextWidgetId;
    if (nextWidget && isHidden) i--;
    if (i === startIndex) nextId = activeId;
  }

  return nextId;
};
